// react
import React from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @react-pdf/renderer
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink } from "@react-pdf/renderer";

// @mui
// @mui
import { Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography, Stack, Tooltip, Box, useTheme } from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon, CheckCircle as CheckCircleIcon, ErrorOutline as ErrorOutlineIcon } from "@mui/icons-material";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const styles = StyleSheet.create({
  page: {
    padding: 28,
    backgroundColor: "#ffffff",
    fontSize: 12,
    color: "#222",
  },
  title: {
    fontSize: 20,
    marginBottom: 16,
    fontWeight: 700,
  },
  section: {
    padding: 14,
    borderRadius: 6,
    border: "1pt solid #e6e6e6",
    marginBottom: 12,
  },
  heading: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 8,
  },
  text: {
    marginBottom: 4,
  },
  success: { color: "#1b8a2f", fontWeight: 600 },
  error: { color: "#b3261e", fontWeight: 600 },
});

const PDFReceiptComponent = ({ uploadReceipt, t }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Text style={styles.title}>{t("submission_report")}</Text>

      <View style={styles.section}>
        <Text style={uploadReceipt.status === "success" ? styles.success : styles.error}>{uploadReceipt.status === "success" ? t("upload_success_message") : t("upload_error_message")}</Text>
        {uploadReceipt.uploadID && (
          <Text style={styles.text}>
            {t("upload_id")}: {uploadReceipt.uploadID}
          </Text>
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.heading}>{t("details")}</Text>
        <Text style={styles.text}>
          {t("performed_by")}: {uploadReceipt.createdByEmail || "N/A"}
        </Text>
        <Text style={styles.text}>
          {t("status")}: {uploadReceipt.status?.toUpperCase() ?? "N/A"}
        </Text>
        <Text style={styles.text}>
          {t("total_docs")}: {uploadReceipt.totalCount ?? "N/A"}
        </Text>
        <Text style={styles.text}>
          {t("valid_docs")}: {uploadReceipt.validCount ?? "N/A"}
        </Text>
        <Text style={styles.text}>
          {t("invalid_docs")}: {uploadReceipt.invalidCount ?? "N/A"}
        </Text>
        <Text style={styles.text}>
          {t("datetime")}: {uploadReceipt.createdAt ? convertTimestamp(uploadReceipt.createdAt) : "N/A"}
        </Text>
      </View>
    </Page>
  </Document>
);

const statusBoxStyle = (theme, isSuccess) => ({
  borderRadius: 3,
  p: 2.2,
  display: "flex",
  alignItems: "center",
  gap: 1.6,
  background: isSuccess ? `${theme.palette.success.main}10` : `${theme.palette.error.main}10`,
  border: `1px solid ${isSuccess ? theme.palette.success.main : theme.palette.error.main}40`,
});

const detailRow = (label, value) => (
  <Stack spacing={0.2}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Stack>
);

const SubmissionReportDialog = ({ uploadReceipt, receiptDialogOpen, handleResetUploadReceipt }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSuccess = uploadReceipt.status === "success";

  return (
    <Dialog
      open={receiptDialogOpen}
      onClose={handleResetUploadReceipt}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
          transition: "all 0.3s ease",
        },
      }}
    >
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" fontWeight={700}>
              {t("submission_report")}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t("close")} placement="top">
              <IconButton onClick={handleResetUploadReceipt} color="error" edge="end">
                <CloseOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <Box sx={(theme) => statusBoxStyle(theme, isSuccess)}>
          {isSuccess ? <CheckCircleIcon color="success" sx={{ fontSize: 28 }} /> : <ErrorOutlineIcon color="error" sx={{ fontSize: 28 }} />}

          <Typography variant="body1" fontWeight={700}>
            {isSuccess ? t("upload_success_message") : t("upload_error_message")}
          </Typography>
        </Box>

        {uploadReceipt.uploadID && <Box sx={{ mt: 2 }}>{detailRow(t("upload_id"), uploadReceipt.uploadID)}</Box>}

        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {t("details")}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              {detailRow(t("performed_by"), uploadReceipt.createdByEmail)}
            </Grid>
            <Grid item xs={6}>
              {detailRow(t("status"), uploadReceipt.status?.toUpperCase())}
            </Grid>
            <Grid item xs={6}>
              {detailRow(t("total_docs"), uploadReceipt.totalCount)}
            </Grid>
            <Grid item xs={6}>
              {detailRow(t("valid_docs"), uploadReceipt.validCount)}
            </Grid>
            <Grid item xs={6}>
              {detailRow(t("invalid_docs"), uploadReceipt.invalidCount)}
            </Grid>
            <Grid item xs={6}>
              {detailRow(t("datetime"), uploadReceipt.createdAt ? convertTimestamp(uploadReceipt.createdAt) : "N/A")}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          variant="text"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handleResetUploadReceipt}
        >
          {t("close")}
        </Button>

        <PDFDownloadLink document={<PDFReceiptComponent uploadReceipt={uploadReceipt} t={t} />} fileName="submission_report.pdf" style={{ textDecoration: "none" }}>
          {({ loading }) => (
            <Button
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 1,
              }}
            >
              {loading ? t("pdf_generating") : t("export_pdf")}
            </Button>
          )}
        </PDFDownloadLink>
      </DialogActions>
    </Dialog>
  );
};

export default SubmissionReportDialog;
